import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Users, Building2,
  ArrowLeft, Eye, EyeOff, ChevronRight,
} from 'lucide-react';

// ─── Role configuration ────────────────────────────────────────────────────────
const ROLES = [
  {
    key: 'student',
    label: 'Student',
    Icon: Users,
    tagline: 'Access your tests, results & performance analytics',
    from: '#7C3AED',
    to: '#4F46E5',
    glow: 'rgba(124,58,237,0.45)',
    bg: 'rgba(124,58,237,0.07)',
    redirect: '/student',
  },
  {
    key: 'coaching_admin',
    label: 'Admin',
    Icon: Building2,
    tagline: 'Manage students, generate tests & view results',
    from: '#059669',
    to: '#0F766E',
    glow: 'rgba(5,150,105,0.45)',
    bg: 'rgba(5,150,105,0.07)',
    redirect: '/coaching-admin',
  },
];

const ROLE_REDIRECTS = {
  super_admin: '/super-admin',
  coaching_admin: '/coaching-admin',
  teacher: '/coaching-admin',
  student: '/student',
};

// Pre-computed so particles don't re-randomise on re-render
const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  size: 2 + (i % 3),
  left: (i * 3.57 + 9) % 100,
  delay: (i * 0.55) % 14,
  duration: 16 + (i % 12),
  drift: ((i % 2 === 0) ? 1 : -1) * (18 + (i % 38)),
}));

// ─── Animated cosmic background ───────────────────────────────────────────────
function AnimatedBg() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none select-none">
      {/* Base */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #07071A 0%, #0D0D2B 50%, #0A0719 100%)' }} />

      {/* Orbs */}
      <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full animate-blob"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)' }} />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full animate-blob animation-delay-2000"
        style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.14) 0%, transparent 70%)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full animate-blob animation-delay-4000"
        style={{ background: 'radial-gradient(circle, rgba(99,37,235,0.12) 0%, transparent 70%)' }} />

      {/* Subtle dot grid */}
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          opacity: 0.4,
        }}
      />

      {/* Rising particles */}
      {PARTICLES.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.left}%`,
            bottom: '-8px',
            background: 'rgba(167,139,250,0.5)',
            animation: `particle-float ${p.duration}s ${p.delay}s infinite linear`,
            '--particle-drift': `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

// ─── 3-D tilt role card ────────────────────────────────────────────────────────
function RoleCard({ role, onSelect, index }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-70, 70], [10, -10]);
  const rotateY = useTransform(mx, [-70, 70], [-10, 10]);

  const onMove = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - (r.left + r.width / 2));
    my.set(e.clientY - (r.top + r.height / 2));
  }, [mx, my]);

  const onLeave = useCallback(() => { mx.set(0); my.set(0); }, [mx, my]);

  return (
    <div style={{ perspective: '900px' }}>
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.88 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, delay: 0.15 + index * 0.13, type: 'spring', stiffness: 180, damping: 22 }}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        whileHover={{ scale: 1.055, transition: { duration: 0.18 } }}
        whileTap={{ scale: 0.96 }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => onSelect(role)}
        className="cursor-pointer group"
      >
        {/* Gradient border wrapper */}
        <div
          className="rounded-2xl p-[1.5px]"
          style={{ background: `linear-gradient(135deg, ${role.from}88, ${role.to}44)` }}
        >
          <div
            className="rounded-[14px] p-7 relative overflow-hidden h-full"
            style={{ background: `linear-gradient(145deg, ${role.bg} 0%, rgba(7,7,26,0.96) 100%)` }}
          >
            {/* Icon box */}
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${role.from}, ${role.to})`,
                boxShadow: `0 8px 24px ${role.glow}`,
                transform: 'translateZ(24px)',
              }}
            >
              <role.Icon size={24} className="text-white" />
            </div>

            <h3 className="text-[19px] font-bold text-white mb-2 leading-snug">{role.label}</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">{role.tagline}</p>

            <div className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: role.from }}>
              <span>Enter portal</span>
              <ChevronRight
                size={15}
                className="group-hover:translate-x-1.5 transition-transform duration-200"
                style={{ color: role.from }}
              />
            </div>

            {/* Hover sheen */}
            <div
              className="absolute inset-0 rounded-[14px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.055) 0%, transparent 55%)' }}
            />

            {/* Bottom accent line */}
            <div
              className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: `linear-gradient(90deg, transparent, ${role.from}, transparent)` }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Login form (shown after role is chosen) ──────────────────────────────────
function LoginForm({ role, onBack, loading, onSubmit }) {
  const [form, setForm] = useState({ coachingCode: '', username: '', password: '' });
  const [showPass, setShowPass] = useState(false);

  const inputCls =
    'w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/20 outline-none transition-all duration-200 ' +
    'bg-white/[0.06] border border-white/10 focus:border-white/25 focus:bg-white/[0.09] ' +
    'focus:ring-2 focus:ring-purple-500/20';

  return (
    <motion.div
      key="form"
      initial={{ opacity: 0, x: 48, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -48, scale: 0.96 }}
      transition={{ duration: 0.32, type: 'spring', stiffness: 280, damping: 28 }}
      className="w-full max-w-sm"
    >
      {/* Back */}
      <motion.button
        onClick={onBack}
        whileHover={{ x: -3 }}
        className="mb-8 flex items-center gap-2 text-gray-500 hover:text-gray-200 transition-colors text-sm group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform duration-150" />
        Choose a different role
      </motion.button>

      {/* Role pill */}
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-[11px] font-bold mb-8 uppercase tracking-[0.15em]"
        style={{ background: `linear-gradient(135deg, ${role.from}, ${role.to})`, boxShadow: `0 4px 20px ${role.glow}` }}
      >
        <role.Icon size={12} />
        {role.label} Portal
      </div>

      {/* Glass card */}
      <div
        className="rounded-2xl border border-white/[0.09] p-8"
        style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(28px)' }}
      >
        <h2 className="text-[22px] font-bold text-white mb-1">Welcome back</h2>
        <p className="text-gray-400 text-sm mb-8">Sign in to your {role.label.toLowerCase()} account</p>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4">
          {/* Coaching Code */}
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-[0.18em] mb-2">
              Coaching Code
            </label>
            <input
              value={form.coachingCode}
              onChange={(e) => setForm(p => ({ ...p, coachingCode: e.target.value.toUpperCase() }))}
              placeholder="e.g. EG-SUNRISE"
              className={inputCls}
              autoComplete="organization"
              required
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-[0.18em] mb-2">
              Username
            </label>
            <input
              value={form.username}
              onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
              placeholder="Your username"
              className={inputCls}
              autoComplete="username"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-[0.18em] mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="••••••••••"
                className={`${inputCls} pr-12`}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPass(p => !p)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-200 transition-colors"
              >
                {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.025, boxShadow: `0 10px 36px ${role.glow}` }}
            whileTap={{ scale: 0.97 }}
            className="w-full py-[15px] rounded-xl text-white font-bold text-sm mt-2 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(135deg, ${role.from}, ${role.to})` }}
          >
            {/* Shimmer on hover */}
            <span
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity"
              style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
            />
            <span className="relative z-10">
              {loading ? 'Signing in…' : `Sign in as ${role.label} →`}
            </span>
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Login() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Public, unauthenticated read -- this page runs before any JWT exists.
  // Admin Login stays hidden while this is still loading (normally well
  // under 200ms) rather than defaulting to shown-then-possibly-hidden --
  // showing it and immediately hiding it again was a worse everyday
  // experience than the rare fetch-failure case fail-open was meant to
  // protect against. Only fails open (assumes shown) on a genuine fetch
  // error, never while merely pending.
  const { data: adminLoginSetting, isLoading: loadingAdminLoginSetting, isError: adminLoginSettingError } = useQuery({
    queryKey: ['admin-login-visibility'],
    queryFn: () => api.get('/settings/admin-login-visibility').then(r => r.data),
  });
  const showAdminLogin = loadingAdminLoginSetting
    ? false
    : adminLoginSettingError
      ? true
      : (adminLoginSetting?.showAdminLogin ?? true);
  const visibleRoles = showAdminLogin ? ROLES : ROLES.filter(r => r.key !== 'coaching_admin');

  const handleSubmit = async (form) => {
    setLoading(true);
    try {
      const user = await login(form.coachingCode, form.username, form.password);
      const to = location.state?.from?.pathname || ROLE_REDIRECTS[user.role] || '/';
      navigate(to, { replace: true });
    } catch (err) {
      // Only ever show "Invalid credentials" for an ACTUAL credentials
      // rejection (401 with a proper { error } body from our own API).
      // Blindly falling back to that string for anything else — a 429 rate
      // limit, a network drop, a 500 — told students their password was
      // wrong when it never was. Confirmed via a live repro: after ~20
      // login attempts from the same IP within 15 minutes (trivial for a
      // whole coaching center sharing one WiFi router during an exam
      // slot), the rate limiter's response has no .error field, and this
      // fallback used to paper over that with a false "Invalid
      // credentials" — the backend's rate-limit response now carries a
      // proper message too (see app.js), but this still needs to not
      // invent a wrong reason if any OTHER non-JSON failure occurs.
      const apiMessage = err.response?.data?.error;
      let message;
      if (apiMessage) {
        message = apiMessage;
      } else if (err.response?.status === 429) {
        message = 'Too many login attempts. Please wait a few minutes and try again.';
      } else if (err.response) {
        message = `Login failed (error ${err.response.status}). Please try again.`;
      } else {
        message = 'Could not reach the server. Please check your connection and try again.';
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-14 relative">
      <AnimatedBg />

      <div className="relative z-10 w-full flex flex-col items-center">
        {/* Logo / brand header */}
        <motion.div
          initial={{ opacity: 0, y: -28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
          className="text-center mb-14"
        >
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #7C3AED, #4F46E5)',
              boxShadow: '0 20px 60px rgba(124,58,237,0.5)',
            }}
          >
            {/* Placeholder mark -- swap for <img src="/logo.svg" className="w-10 h-10 object-contain" alt="My Learning Graph" />
                once the real logo file is available; this box's size/gradient/shadow can stay as-is around it. */}
            <span className="text-4xl select-none">⚡</span>
          </motion.div>
          <h1 className="text-[42px] font-extrabold text-white tracking-tight leading-none">
            My Learning Graph
          </h1>
          <p className="text-gray-500 mt-2.5 text-[15px]">
            The premium coaching test management platform
          </p>
        </motion.div>

        {/* Main content: role selection ↔ login form */}
        <AnimatePresence mode="wait">
          {!selectedRole ? (
            <motion.div
              key="select"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.22 }}
              className="w-full max-w-[860px]"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-center text-[11px] font-bold text-gray-600 uppercase tracking-[0.22em] mb-9"
              >
                Choose your portal to continue
              </motion.p>

              <div className={`grid gap-5 mx-auto ${
                visibleRoles.length > 1 ? 'grid-cols-1 sm:grid-cols-2 max-w-[580px]' : 'grid-cols-1 max-w-[280px]'
              }`}>
                {visibleRoles.map((role, i) => (
                  <RoleCard key={role.key} role={role} index={i} onSelect={setSelectedRole} />
                ))}
              </div>

            </motion.div>
          ) : (
            <LoginForm
              key="form"
              role={selectedRole}
              onBack={() => setSelectedRole(null)}
              loading={loading}
              onSubmit={handleSubmit}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
